import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, Loader2, Calendar, MessageCircle } from 'lucide-react';

import NotFound from '@/pages/NotFound';
import { useStaticPage } from '@/hooks/useStaticPage';
import { fadeUp, viewportOnce } from '@/lib/motion';
import { PrimaryButton, SecondaryButton } from '@/components/brand/Buttons';

const EASE = [0.22, 1, 0.36, 1];

function formatUpdated(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return ''; }
}

/* ─── Body renderer ──────────────────────────────────────────────────────── */
function renderParagraphs(text) {
  if (text.startsWith('•')) {
    const lines = text.split('\n').filter(Boolean);
    return (
      <ul className="space-y-2">
        {lines.map((line, li) => (
          <li key={li} className="flex gap-2 text-slatebody text-[15px] leading-relaxed">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C9962E] flex-shrink-0" />
            <span>{line.replace(/^•\s*/, '')}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-slatebody text-[15px] leading-relaxed whitespace-pre-line">{text}</p>;
}

function BodyRenderer({ body }) {
  const blocks = (body || '').split('\n\n').filter(Boolean);
  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        if (block.startsWith('## ')) {
          const nl = block.indexOf('\n');
          const heading = (nl === -1 ? block : block.slice(0, nl)).replace(/^##\s*/, '');
          const rest = nl === -1 ? '' : block.slice(nl + 1);
          return (
            <div key={i}>
              <h2 className="text-xl sm:text-2xl font-bold text-navy-deep mb-4">{heading}</h2>
              {rest && <div className="space-y-4">{renderParagraphs(rest)}</div>}
            </div>
          );
        }
        return <div key={i}>{renderParagraphs(block)}</div>;
      })}
    </div>
  );
}

/* ─── Hero ────────────────────────────────────────────────────────────────── */
function PageHeroSection({ eyebrow, title, lastUpdated, updatedAt }) {
  const displayDate = updatedAt ? formatUpdated(updatedAt) : lastUpdated;
  return (
    <section className="relative pt-48 pb-14 overflow-hidden">
      <div className="absolute inset-0 bg-sky-100" />
      <div className="absolute inset-0 bg-hero-mesh bg-[length:200%_200%] animate-mesh-pan opacity-70" />
      <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="legaldots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.1" fill="#2F6FB0" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#legaldots)" />
      </svg>

      <div className="container mx-auto relative">
        {/* Breadcrumb — left aligned */}
        <motion.nav
          aria-label="Breadcrumb"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="flex items-center gap-1.5 text-[12px] text-slatebody flex-wrap mb-8"
        >
          <Link to="/" className="hover:text-brandblue transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slatebody/50" />
          <span className="font-semibold text-navy-deep">{title}</span>
        </motion.nav>

        {/* Centre-aligned heading block */}
        <div className="text-center">
          {/* LARGE: category / eyebrow — the primary headline */}
          {eyebrow && (
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-navy-deep leading-[1.06]"
            >
              {eyebrow}
            </motion.h1>
          )}

          {/* SMALLER: page-specific title */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15, ease: EASE }}
            className="mt-3 text-xl sm:text-2xl font-semibold text-brandblue tracking-tight"
          >
            {title}
          </motion.p>

          {/* Gradient divider — centred */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
            style={{ transformOrigin: 'center' }}
            className="mt-5 h-[2px] w-24 rounded-full bg-gradient-to-r from-[#2BA8E0] via-[#2F6FB0] to-[#E8C25A] mx-auto"
          />
        </div>

        {/* Last updated pill — left-aligned, below the heading block */}
        {displayDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.45, ease: EASE }}
            className="mt-7 inline-flex items-center gap-2 text-[13px] text-slatebody bg-white/70 backdrop-blur-sm border border-hairline rounded-full px-4 py-1.5"
          >
            <Calendar className="h-3.5 w-3.5 text-brandblue" />
            Last updated:&nbsp;<span className="font-semibold text-navy-deep">{displayDate}</span>
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────────── */
export default function StaticPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: cmsData, loading, notFound } = useStaticPage(slug);

  const page = cmsData?.data;

  useEffect(() => {
    if (!page?.title) return;
    document.title = page.metaTitle || `${page.title} — Max Global Software`;
    if (page?.metaDescription) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', 'description'); document.head.appendChild(el); }
      el.setAttribute('content', page.metaDescription);
    }
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-32 pb-20">
        <Loader2 className="h-6 w-6 animate-spin text-slatebody/60" />
      </div>
    );
  }

  if (notFound || !page) return <NotFound />;

  return (
    <article data-testid="static-page">
      <PageHeroSection
        title={page.title}
        updatedAt={page.updatedAt}
      />

      <div className="bg-white py-16">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
          >
            {page.body && <BodyRenderer body={page.body} />}
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-14 pt-8 border-t border-hairline flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <SecondaryButton
              icon={<ChevronLeft className="h-4 w-4" />}
              onClick={() => navigate('/')}
            >
              Back to Home
            </SecondaryButton>
            <PrimaryButton
              icon={<MessageCircle className="h-4 w-4" />}
              onClick={() => navigate('/contact')}
            >
              Questions? Contact Us
            </PrimaryButton>
          </motion.div>
        </div>
      </div>
    </article>
  );
}
